import { Routes, Route, Navigate, useNavigate } from "react-router-dom";

import { observer } from "mobx-react-lite";
import React, { Suspense, lazy, useEffect } from "react";
import { useStore } from "./utils/helpers";
import { PublicRoute } from "./routes/helpers";
import "react-datetime/css/react-datetime.css";
import process from "process";

var global;
if (typeof process !== "undefined") {
  global = window;
}

global.process = process;

// Suspense fallback shown while a lazy route chunk loads.
const Loading = () => <div style={{ padding: 24 }}>Loading…</div>;

// Route-level code splitting via React.lazy (replaces react-loadable).
const AuthorizedRoutes = lazy(() => import("routes/all"));

// const GeneralLayout = Loadable({
//   loader: () => import("./components/Layouts/AuthenticatedLayout"),
//   loading: Loading,
//   render(loaded, props) {
//     const C: any = loaded.AuthenticatedLayout;
//     return <C {...(props as any)} />;
//   },
// });
// const ResetPassword  = Loadable({
//   loader: () => import("./pages/Auth/ResetPassword"),
//   loading: Loading,
//   render(loaded, props) {
//     const C: any = loaded.default;
//     return <C {...(props as any)} />;
//   },
// });

const ResetPassword = lazy(() => import("./pages/Auth/ResetPassword"));
const Signin = lazy(() => import("./pages/Auth/Login"));
const Logout = lazy(() => import("./pages/Auth/Logout"));
// Super-admin client picker (RealSoftware staff). Gates on super-admin itself.
const SwitchClient = lazy(() => import("./pages/Auth/SwitchClient"));

// v2 experience — its own chrome (AppShell), lazily loaded so its Tailwind bundle
// and scoped stylesheet never ship until someone visits /v2/*. Mounted here at the
// root, OUTSIDE the legacy AuthenticatedLayout, and as a PublicRoute because V2App
// gates auth itself (it waits for hasCheckedAuth before redirecting, avoiding the
// premature bounce-to-/login that PrivateRoute causes on a hard load).
const V2App = lazy(() => import("v2/App"));

// External self-service portal (owners / tenants / vendors). Lazily loaded, its
// own session (separate token), and a PublicRoute because it gates its own auth
// — staff are never required here.
const PortalApp = lazy(() => import("portal/PortalApp"));


const App: React.FC = observer((props) => {
  const rootStore = useStore();
  const { userStore } = rootStore;
  const { hasCheckedAuth } = userStore;

  const navigate = useNavigate();

  // Mid-session token expiry: the axios interceptor broadcasts on any 401
  // (previously requests just failed silently forever — empty tables, no
  // logout). Log out and return to login remembering where the user was.
  // Skipped for the external client portal (own session) and when not logged
  // in (a failed login attempt also 401s).
  useEffect(() => {
    const onUnauthorized = () => {
      if (!userStore.isLoggedIn) return;
      if (window.location.pathname.startsWith("/portal")) return;
      const from = window.location.pathname + window.location.search;
      // Remember WHO was interrupted, not just where: LoginScreen only resumes
      // `from` when the same account signs back in, so a different user on a
      // shared machine can't be dropped into the previous user's record page
      // (the cross-client URL leak this used to force-fix with a blanket
      // navigate("/crm/") on every auth flip).
      const fromEmail =
        userStore.email || window.localStorage.getItem("__username") || null;
      userStore.logout();
      navigate("/login", { replace: true, state: { from, fromEmail } });
    };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, [userStore, navigate]);

  // Safari keeps the native <input type="date"> calendar popover open after a
  // day is clicked (Chrome/Firefox close it). The popover is browser UI with no
  // close API, but it dismisses when the input loses focus — so blur on commit.
  // One document-level listener covers every date field, legacy pages and /v2
  // alike ("change" bubbles, and fires after React's own onChange has run).
  // Safari-only: blurring on change would eject Chrome users typing a date via
  // the keyboard mid-entry. Deliberately not datetime-local — there the picker
  // must stay open for the time part.
  useEffect(() => {
    const isSafari =
      /safari/i.test(navigator.userAgent) &&
      !/chrome|chromium|crios|edg|fxios|android/i.test(navigator.userAgent);
    if (!isSafari) return;
    const closePickerOnSelect = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t instanceof HTMLInputElement && t.type === "date") t.blur();
    };
    document.addEventListener("change", closePickerOnSelect);
    return () => document.removeEventListener("change", closePickerOnSelect);
  }, []);

  // Per-surface document title. The client-portal subdomain (my.*) is the Owner
  // Portal (owners / tenants / vendors); crm.* (and everything else) is the
  // staff/agent app.
  useEffect(() => {
    document.title = /^my\./i.test(window.location.hostname)
      ? "realCRM Owner Portal | Real Software"
      : "realCRM Agent Portal | Real Software";
  }, []);

  // Rehydrate the stored session once on mount. Deliberately NO navigation on
  // the not-authorized -> authorized transition: checkLogin() flips it on EVERY
  // hard load of a signed-in tab, so a redirect here hijacks deep links (the
  // CMS "Open CRM" /switch-client hand-off, any /v2/* refresh) into the v1 CRM.
  // Fresh sign-ins pick their own destination in LoginScreen.completeLogin(),
  // which also guards the cross-client URL leak the old redirect was for (it
  // only resumes state.from when the SAME account signs back in).
  useEffect(() => {
    if (!hasCheckedAuth) {
      userStore.checkLogin();
    }
  }, [hasCheckedAuth, userStore]);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<PublicRoute component={Signin} />} />
        <Route path="/logout" element={<PublicRoute component={Logout} />} />
        <Route path="/switch-client" element={<PublicRoute component={SwitchClient} />} />
        <Route
          path="/reset-password"
          element={<PublicRoute component={ResetPassword} />}
        />

        <Route path="/v2/*" element={<PublicRoute component={V2App} />} />
        <Route path="/portal/*" element={<PublicRoute component={PortalApp} />} />
        {/* On the client-portal subdomain (my.*), the bare root is the portal,
            not the staff app. crm.* and everything else are unaffected. */}
        {/^my\./i.test(window.location.hostname) ? (
          <Route path="/" element={<Navigate to="/portal" replace />} />
        ) : null}

        <Route path="*" element={<PublicRoute component={AuthorizedRoutes} />} />
      </Routes>
    </Suspense>
  );
});

export default App;
